// Function index:
//   FACE_W                 — tile face display width (flat-to-flat) in px.
//   FACE_H                 — tile face display height (point-to-point) in px.
//   FACE_CENTRE_FROM_BOTTOM_FRAC — where the face centre sits inside the tile
//       canvas, as a fraction of canvas height measured up from the bottom.
//   isoProject(q, r)       — pointy-top axial (q,r) → face-centre ground point.
//   screenToAxial(sx, gY)  — inverse: ground point → nearest axial (q,r).
//   isoHexCorners(q, r)    — the six corners of hex (q,r)'s face outline in
//       ground space — for overlay rings and the renderer's vector fallback.
//
// Why: the grid and the painterly tiles are BOTH pointy-top, so there is no
// rotation between them. What there is, is vertical foreshortening: measured
// across all six shipped tiles (web/public/atlases/terrain/*.png) the face is
// 256 px wide flat-to-flat but only 254 px point-to-point, where a regular
// pointy-top hex would be 296. So the lattice is anisotropic — a regular hex
// grid squashed vertically — and cannot be expressed as a rotation+scale.
// Deriving it from the art's own proportions is what makes the tiles
// tessellate; assuming a regular grid leaves bowtie gaps between every tile.

// Measured from the shipped tiles. Canvas is 256x384; the face's vertical side
// edges span y=191..321 and its bottom vertex sits on the canvas bottom edge
// (y=383), which puts the face centre at y=256 — i.e. 128 px above the bottom,
// exactly one third of the canvas height. The transparent headroom above is
// where tall features (mountain peaks, treetops) live.
const ART_FACE_W = 256;
const ART_FACE_H = 254;
const ART_CANVAS_H = 384;
const ART_FACE_CENTRE_Y = 256;

export const FACE_W = 138;
export const FACE_H = FACE_W * (ART_FACE_H / ART_FACE_W);

// Tiles are bottom-anchored (anchor 0.5,1) so that tall art extends upward out
// of the headroom; the renderer lifts each sprite by this fraction of its drawn
// height to put the FACE CENTRE — not the canvas bottom — on the ground point.
export const FACE_CENTRE_FROM_BOTTOM_FRAC = (ART_CANVAS_H - ART_FACE_CENTRE_Y) / ART_CANVAS_H;

// Pointy-top axial → pixel, with the art's own width/height instead of the
// regular-hex √3/2 relationship:
//   east (+q)      is one full face width across
//   south-east(+r) is half a width across and three-quarters of a height down
const ROW_STEP = 0.75 * FACE_H;

export function isoProject(q: number, r: number): { sx: number; groundY: number } {
  return { sx: FACE_W * (q + r / 2), groundY: ROW_STEP * r };
}

export function screenToAxial(sx: number, groundY: number): { q: number; r: number } {
  const rf = groundY / ROW_STEP;
  const qf = sx / FACE_W - rf / 2;
  return cubeRound(qf, rf);
}

// Pointy-top hex centred on its ground point: vertices top and bottom, vertical
// side edges spanning ±FACE_H/4 (which reproduces the measured 191..321 band).
export function isoHexCorners(q: number, r: number): Array<{ x: number; y: number }> {
  const { sx, groundY } = isoProject(q, r);
  const hw = FACE_W / 2;
  const quarter = FACE_H / 4;
  const half = FACE_H / 2;
  return [
    { x: sx, y: groundY - half },
    { x: sx + hw, y: groundY - quarter },
    { x: sx + hw, y: groundY + quarter },
    { x: sx, y: groundY + half },
    { x: sx - hw, y: groundY + quarter },
    { x: sx - hw, y: groundY - quarter },
  ];
}

function cubeRound(qf: number, rf: number): { q: number; r: number } {
  const xf = qf, zf = rf, yf = -xf - zf;
  let x = Math.round(xf), y = Math.round(yf), z = Math.round(zf);
  const dx = Math.abs(x - xf), dy = Math.abs(y - yf), dz = Math.abs(z - zf);
  if (dx > dy && dx > dz) x = -y - z;
  else if (dy > dz) y = -x - z;
  else z = -x - y;
  return { q: x, r: z };
}
